import type { Flow } from "@learnengine/shared-types";

export interface ProgressSnapshot {
  completedSeconds: number;
  totalSeconds: number;
  completionPercent: number; // 0..100
  activeSeconds: number;
}

/**
 * ProgressTracker tracks completed duration and exposes a simple snapshot.
 * View logic (e.g. what counts as a "view") is handled later in the backend.
 */
export class ProgressTracker {
  private flow: Flow | null = null;
  private completedSeconds = 0;
  private totalSeconds = 0;
  private activeSeconds = 0;
  private isPlaying = false;
  private lastTimeSeconds = 0;

  loadFlow(flow: Flow) {
    this.flow = flow;
    this.completedSeconds = 0;
    this.totalSeconds = flow.nodes.reduce((sum, n) => sum + n.durationSeconds, 0);
    this.activeSeconds = 0;
    this.isPlaying = false;
    this.lastTimeSeconds = 0;
  }

  play(currentTimeSeconds: number) {
    this.isPlaying = true;
    this.lastTimeSeconds = currentTimeSeconds;
  }

  pause(currentTimeSeconds: number) {
    // Update once so active time is accurate at the pause boundary.
    this.update(currentTimeSeconds);
    this.isPlaying = false;
    this.lastTimeSeconds = currentTimeSeconds;
  }

  /**
   * Seek is a discontinuous jump in the timeline (scrub).
   * It must not count towards active watch time.
   */
  seek(currentTimeSeconds: number) {
    if (!this.flow) return;
    const clamped = Math.max(0, Math.min(currentTimeSeconds, this.totalSeconds));
    this.lastTimeSeconds = clamped;
    this.completedSeconds = clamped;
  }

  /**
   * Restore progress from persistence (Phase 3).
   * This must not add watch time; it sets the internal counters directly.
   */
  restore(snapshot: { completedSeconds: number; activeSeconds: number }) {
    if (!this.flow) return;
    const clampedCompleted = Math.max(0, Math.min(snapshot.completedSeconds, this.totalSeconds));
    const clampedActive = Math.max(0, snapshot.activeSeconds);
    this.completedSeconds = clampedCompleted;
    this.activeSeconds = clampedActive;
    this.lastTimeSeconds = clampedCompleted;
  }

  /**
   * Call when the global timeline time changes.
   */
  update(currentTimeSeconds: number) {
    if (!this.flow) return;
    const clamped = Math.max(0, Math.min(currentTimeSeconds, this.totalSeconds));

    if (this.isPlaying) {
      const delta = Math.max(0, clamped - this.lastTimeSeconds);
      this.activeSeconds += delta;
    }

    this.lastTimeSeconds = clamped;
    this.completedSeconds = clamped;
  }

  snapshot(): ProgressSnapshot {
    if (!this.flow) {
      return { completedSeconds: 0, totalSeconds: 0, completionPercent: 0, activeSeconds: 0 };
    }
    const completionPercent = this.totalSeconds === 0 ? 0 : (this.completedSeconds / this.totalSeconds) * 100;
    return {
      completedSeconds: this.completedSeconds,
      totalSeconds: this.totalSeconds,
      completionPercent,
      activeSeconds: this.activeSeconds,
    };
  }
}
